import { createContext } from "react";

import { getDay, isSameDay } from "date-fns";
import { makeAutoObservable, runInAction } from "mobx";

import client from "@/client";
import { graphql } from "@/gql/gql";
import {
  CreateWorkScheduleInput,
  RuleKind,
  UpdateWorkScheduleInput,
} from "@/gql/graphql";

import Interval from "./Interval";
import Rule from "./Rule";
import { minutesOfTimeOfDay } from "../utils/calendar";

// Weekday names in getDay() order (0 = Sunday). Matches the backend's
// Date::DAYNAMES, which is what Rule#wday is validated against and stores.
const DAYNAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MAX_HISTORY = 50;

// Plain-data snapshots for undo/redo. Times are stored as epoch ms so a
// restored Interval is an exact copy (ids and _destroy preserved for correct
// persistence on save).
type IntervalSnap = {
  id?: string;
  start: number | null;
  end: number | null;
  destroy?: boolean;
};
type RuleSnap = {
  id?: string;
  ruleType: RuleKind;
  wday?: string | null;
  date: number | null;
  destroy?: boolean;
  intervals: IntervalSnap[];
};
type ScheduleSnap = {
  id: string;
  wdayRules: RuleSnap[];
  dateRules: RuleSnap[];
};

export default class Schedule {
  id = "";
  wdayRules: Rule[] = DAYNAMES.map((name) => Rule.wday(this, name));
  dateRules: Rule[] = [];
  submitMode = "";
  history: ScheduleSnap[] = [];
  future: ScheduleSnap[] = [];

  get isFormValid(): boolean {
    return (
      this.wdayRules.every((wday) => wday.isValid) &&
      this.dateRules.every((dateRules) => dateRules.isValid)
    );
  }

  get canUndo(): boolean {
    return this.history.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  get mode(): string {
    return this.submitMode;
  }

  constructor() {
    makeAutoObservable(this);
  }

  load = async () => {
    const { data } = await client.query({
      query: scheduleQuery,
    });

    runInAction(() => {
      if (!data.coachViewer.workSchedule) return;

      const rules = data?.coachViewer.workSchedule?.rules ?? [];
      this.id = data.coachViewer.workSchedule?.id ?? "";

      // The backend's `rules` association has no explicit ordering, so the
      // weekday rules can come back in any order (and only some weekdays may be
      // present). Rebuild wdayRules keyed by weekday name in a fixed
      // Sunday..Saturday order, filling any missing day with an empty rule, so
      // both the list view (day ordering) and the calendar (which maps a date's
      // weekday to a rule) stay correct regardless of server order.
      const wdayFragments = new Map(
        rules
          .filter((rule) => rule.ruleType === RuleKind.Wday)
          .map((rule) => [rule.wday, rule])
      );
      this.wdayRules = DAYNAMES.map((name) => {
        const fragment = wdayFragments.get(name);
        return fragment
          ? Rule.fromFragment(this, fragment)
          : Rule.wday(this, name);
      });
      this.dateRules = rules
        .filter((rule) => rule.ruleType === RuleKind.Date)
        .map((rul) => Rule.fromFragment(this, rul));

      // Freshly loaded state is the new undo baseline.
      this.history = [];
      this.future = [];
    });
  };

  // --- Undo / redo ---------------------------------------------------------
  // Call before any edit so it can be reverted. Gestures snapshot once at the
  // start (not per pointermove), giving one undo step per gesture/action.
  pushHistory = () => {
    this.history.push(this.snapshot());
    if (this.history.length > MAX_HISTORY) this.history.shift();
    this.future = [];
  };

  undo = () => {
    const previous = this.history.pop();
    if (!previous) return;
    this.future.push(this.snapshot());
    this.restore(previous);
  };

  redo = () => {
    const next = this.future.pop();
    if (!next) return;
    this.history.push(this.snapshot());
    this.restore(next);
  };

  private snapshotRule(rule: Rule): RuleSnap {
    return {
      id: rule.id,
      ruleType: rule.ruleType,
      wday: rule.wday,
      date: rule.date ? rule.date.getTime() : null,
      destroy: rule._destroy,
      intervals: rule.intervals.map((interval) => ({
        id: interval.id,
        start: interval.startTime ? interval.startTime.getTime() : null,
        end: interval.endTime ? interval.endTime.getTime() : null,
        destroy: interval._destroy,
      })),
    };
  }

  private snapshot(): ScheduleSnap {
    return {
      id: this.id,
      wdayRules: this.wdayRules.map((rule) => this.snapshotRule(rule)),
      dateRules: this.dateRules.map((rule) => this.snapshotRule(rule)),
    };
  }

  private restoreRule(snap: RuleSnap): Rule {
    const rule = new Rule(this, snap.ruleType);
    rule.id = snap.id;
    rule.wday = snap.wday;
    rule.date = snap.date ? new Date(snap.date) : undefined;
    rule._destroy = snap.destroy;
    rule.intervals = snap.intervals.map((intervalSnap) => {
      const interval = new Interval();
      interval.id = intervalSnap.id;
      interval.startTime = intervalSnap.start
        ? new Date(intervalSnap.start)
        : null;
      interval.endTime = intervalSnap.end ? new Date(intervalSnap.end) : null;
      interval._destroy = intervalSnap.destroy;
      return interval;
    });
    return rule;
  }

  private restore(snap: ScheduleSnap) {
    this.id = snap.id;
    this.wdayRules = snap.wdayRules.map((rule) => this.restoreRule(rule));
    this.dateRules = snap.dateRules.map((rule) => this.restoreRule(rule));
  }

  // Coalesce overlapping/adjacent intervals within a rule into single blocks.
  mergeOverlaps = (rule: Rule) => {
    const present = rule.presentIntervals.filter((i) => i.interval);
    if (present.length < 2) return;

    const sorted = [...present].sort(
      (a, b) =>
        minutesOfTimeOfDay(a.startTime!) - minutesOfTimeOfDay(b.startTime!)
    );
    const merged: { start: Date; end: Date }[] = [];
    sorted.forEach((interval) => {
      const start = interval.startTime!;
      const end = interval.endTime!;
      const last = merged[merged.length - 1];
      if (last && minutesOfTimeOfDay(start) <= minutesOfTimeOfDay(last.end)) {
        if (minutesOfTimeOfDay(end) > minutesOfTimeOfDay(last.end))
          last.end = end;
      } else {
        merged.push({ start, end });
      }
    });

    if (merged.length === present.length) return; // nothing to merge

    this.pushHistory();
    rule.removeIntervals();
    merged.forEach((block) =>
      rule.addIntervalWithTimes(block.start, block.end)
    );
  };

  findOverrideRule(date: Date | null | undefined) {
    if (date == null) return undefined;

    return this.dateRules.find(
      (r) => !r._destroy && r.date && isSameDay(r.date, date)
    );
  }

  // The recurring weekly rule for a date's weekday, matched by weekday name so
  // it is correct regardless of the order wdayRules happens to be in.
  wdayRuleForDate(date: Date): Rule {
    const name = DAYNAMES[getDay(date)];
    return (
      this.wdayRules.find((rule) => rule.wday === name) ??
      this.wdayRules[getDay(date)]
    );
  }

  // The rule whose intervals actually apply on a given calendar date: a date
  // override when one exists, otherwise the recurring weekday rule. The
  // calendar reads and edits availability through this so a day with an
  // exception is edited as the exception, and a normal day edits the weekly
  // pattern.
  effectiveRuleForDate(date: Date): { rule: Rule; isOverride: boolean } {
    const override = this.findOverrideRule(date);
    if (override) return { rule: override, isOverride: true };
    return { rule: this.wdayRuleForDate(date), isOverride: false };
  }

  // Materialize a date override for the given date, seeded from that weekday's
  // current recurring intervals, so the calendar can edit a single date
  // independently of the weekly pattern. Restores a previously removed override
  // for the same date instead of creating a duplicate.
  ensureOverrideForDate = (date: Date): Rule => {
    const existing = this.dateRules.find(
      (r) => r.date && isSameDay(r.date, date)
    );
    if (existing) {
      existing._destroy = false;
      existing.restoreIntervals();
      return existing;
    }

    const rule = new Rule(this, RuleKind.Date);
    rule.setDate(date);
    rule.intervals = this.wdayRuleForDate(date).presentIntervals.map((i) => {
      const copy = new Interval();
      copy.startTime = i.startTime ? new Date(i.startTime) : i.startTime;
      copy.endTime = i.endTime ? new Date(i.endTime) : i.endTime;
      return copy;
    });
    this.dateRules.push(rule);
    return rule;
  };

  saveOverrideRule = (rule: Rule) => {
    if (rule.ruleType !== RuleKind.Date) return;

    // If the date already exists, we'll replace the intervals of the existing
    // rule instead.
    const existing = this.findOverrideRule(rule.date);
    if (existing) {
      // // Mark for destroy all existing intervals and push new intervals.
      existing.removeIntervals();
      existing.intervals.push(...rule.intervals);
    } else {
      this.dateRules.push(rule);
    }
  };

  removeOverrideRule(rule: Rule) {
    this.dateRules.splice(this.dateRules.indexOf(rule), 1);
  }

  copyIntervals = (from: Rule, recipients: Rule[]) => {
    recipients.forEach((recipient) => {
      recipient.intervals = from.intervals.map((interval) => {
        const newInterval = new Interval();
        newInterval.startTime = interval.startTime;
        newInterval.endTime = interval.endTime;
        newInterval._destroy = interval._destroy;
        return newInterval;
      });
    });
  };

  get combinedRules(): Rule[] {
    return [...this.wdayRules, ...this.dateRules];
  }

  toCreateWorkScheduleInput(coachId: string): CreateWorkScheduleInput {
    const combinedRules = this.combinedRules;
    const createWorkScheduleInput: CreateWorkScheduleInput = {
      attributes: {
        coachId: coachId,
        rulesAttributes: combinedRules?.map((rule: Rule) => {
          return {
            ...rule.toAttributes(),
            intervalsAttributes: rule.intervals?.map((interval: Interval) => {
              return interval.toAttributes();
            }),
          };
        }),
      },
    };

    return createWorkScheduleInput;
  }

  toUpdateWorkScheduleInput(): UpdateWorkScheduleInput {
    const combinedRules = this.combinedRules;
    const updateWorkScheduleInput: UpdateWorkScheduleInput = {
      id: this.id,
      patch: {
        rulesAttributes: combinedRules?.map((rule) => {
          return {
            ...rule.toAttributes(),
            intervalsAttributes: rule.intervals?.map((interval) => {
              return interval.toAttributes();
            }),
          };
        }),
      },
    };

    return updateWorkScheduleInput;
  }
}

const scheduleQuery = graphql(`
  query ScheduleQuery {
    coachViewer {
      id
      workSchedule {
        ...CoachWorkSchedule
      }
    }
  }

  fragment CoachWorkSchedule on WorkSchedule {
    id
    coachId
    rules {
      ...Rule
    }
  }

  fragment Rule on Rule {
    id
    date
    ruleType
    wday
    intervals {
      ...Interval
    }
  }

  fragment Interval on Interval {
    id
    ruleId
    startTime
    endTime
  }
`);

export const ScheduleContext = createContext<Schedule | undefined>(undefined);
